import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api';
import { createSafeHtml } from '../../utils/sanitize';
import { useAuth } from '../../context/AuthContext';
import TopBar from '../../Components/TopBar';
import Navbar from '../../Components/Navbar';
import Footer from '../../Components/Footer';
import './CourseDetailsPage.css';

const DESCRIPTION_PREVIEW_MAX_CHARS = 420;

const formatDate = value => {
  if (!value) return 'TBA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString();
};

const isEnrollIntake = intake => {
  if (!intake || intake.status !== 'open') return false;
  if (!intake.end_date) return true;
  const end = new Date(intake.end_date);
  const today = new Date();
  end.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return end >= today;
};

const LEVEL_OF_STUDIES_OPTIONS = [
  { value: 'bachelors_degree', label: "Bachelor's degree" },
  { value: 'masters_degree', label: "Master's Degree" },
  { value: 'university_student_last_level', label: 'University Student at the last level' },
];

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'employed', label: 'Employed' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'student', label: 'Student' },
  { value: 'business_owner', label: 'Business Owner' },
];

const TRAINING_TIME_OPTIONS = [
  { value: 'day_program', label: 'Day Program (Monday to Friday, 9:00 AM to 12:00 PM)' },
  { value: 'weekend_program', label: 'Weekend Program (Saturday and Sunday, 9:00 AM to 1:00 PM)' },
  { value: 'evening_program', label: 'Evening Program (Monday to Friday, 6:00 PM to 8:00 PM)' },
  { value: 'online_program', label: 'Online (Monday to Friday, 6:00 PM to 8:00 PM)' },
  { value: 'day_afternoon_session', label: 'Day Afternoon Session (Monday to Friday, 2:00 PM to 4:00 PM)' },
];

const SKILL_LEVEL_OPTIONS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'momo_pay', label: 'MOMO PAY (1708263) ICB-FINANCE EXPERTS Ltd' },
  {
    value: 'bank_transfer',
    label: 'Transfer to BK Account 100154516241 under ICB-FINANCE EXPERTS Ltd',
  },
];

const normalizeDescription = value => {
  const raw = String(value || '').replace(/\r\n/g, '\n').trim();
  if (!raw) return '';

  return raw
    .replace(/<\s*li\b[^>]*>/gi, '- ')
    .replace(/<\s*\/(p|div|li|h[1-6])\s*>/gi, '\n')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const stripHtml = value =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const parseDescriptionBlocks = description => {
  const normalized = normalizeDescription(description);
  if (!normalized) return [];

  const lines = normalized.split('\n');
  const blocks = [];
  let paragraphLines = [];
  let listItems = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    blocks.push({ type: 'paragraph', text: paragraphLines.join(' ').trim() });
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push({ type: 'list', items: [...listItems] });
    listItems = [];
  };

  lines.forEach(rawLine => {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      return;
    }

    const headingMatch = line.match(/^#{1,6}\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', text: headingMatch[1].trim() });
      return;
    }

    const numberedListMatch = line.match(/^\d+[.)]\s+(.+)$/);
    const bulletMatch = line.match(/^[-*•]\s+(.+)$/);
    if (numberedListMatch || bulletMatch) {
      flushParagraph();
      listItems.push((numberedListMatch?.[1] || bulletMatch?.[1] || '').trim());
      return;
    }

    if (line.endsWith(':') && line.length <= 100) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', text: line.slice(0, -1).trim() });
      return;
    }

    flushList();
    paragraphLines.push(line);
  });

  flushParagraph();
  flushList();
  return blocks;
};

const getDescriptionPreview = (blocks, maxChars = DESCRIPTION_PREVIEW_MAX_CHARS) => {
  if (!blocks.length) return { previewBlocks: [], isTruncated: false };

  let consumedChars = 0;
  const previewBlocks = [];

  for (const block of blocks) {
    if (consumedChars >= maxChars) break;

    if (block.type === 'list') {
      const items = [];
      for (const item of block.items) {
        if (consumedChars >= maxChars) break;
        items.push(item);
        consumedChars += item.length;
      }
      if (items.length) {
        previewBlocks.push({ ...block, items });
      }
      continue;
    }

    previewBlocks.push(block);
    consumedChars += (block.text || '').length;
  }

  const totalChars = blocks.reduce((sum, block) => {
    if (block.type === 'list') {
      return sum + block.items.join(' ').length;
    }
    return sum + (block.text || '').length;
  }, 0);

  return { previewBlocks, isTruncated: totalChars > maxChars };
};

const CourseDescriptionContent = ({ blocks }) => {
  if (!blocks.length) {
    return <p className="lms-description-paragraph">Course description will be updated soon.</p>;
  }

  return (
    <>
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          return (
            <h3 key={`desc-heading-${index}`} className="lms-description-heading">
              {block.text}
            </h3>
          );
        }

        if (block.type === 'list') {
          return (
            <ul key={`desc-list-${index}`} className="lms-description-list">
              {block.items.map((item, itemIndex) => (
                <li key={`desc-list-item-${index}-${itemIndex}`}>{item}</li>
              ))}
            </ul>
          );
        }

        return (
          <p key={`desc-paragraph-${index}`} className="lms-description-paragraph">
            {block.text}
          </p>
        );
      })}
    </>
  );
};

const CourseDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const selectedIntakeFromQuery = new URLSearchParams(location.search).get('intake');

  const [course, setCourse] = useState(null);
  const [courseIntakeId, setCourseIntakeId] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [levelOfStudies, setLevelOfStudies] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('');
  const [favorableTrainingTime, setFavorableTrainingTime] = useState('');
  const [accountingSkillLevel, setAccountingSkillLevel] = useState('');
  const [excelSkillLevel, setExcelSkillLevel] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [packageType, setPackageType] = useState('full_package');
  const [paymentPlan, setPaymentPlan] = useState('full');
  const [paymentReference, setPaymentReference] = useState('');
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [applicationDocuments, setApplicationDocuments] = useState([]);
  const [notes, setNotes] = useState('');
  const [paymentProof, setPaymentProof] = useState(null);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [isPaymentInstructionExpanded, setIsPaymentInstructionExpanded] = useState(false);

  const userFullName = user?.fullName || user?.name || '';
  const userEmail = user?.email || '';

  useEffect(() => {
    if (userFullName && !fullName) {
      setFullName(userFullName);
    }
  }, [userFullName, fullName]);

  useEffect(() => {
    if (userEmail && !email) {
      setEmail(userEmail);
    }
  }, [userEmail, email]);

  useEffect(() => {
    if (user?.phone && !phone) {
      setPhone(user.phone);
    }
  }, [user, phone]);

  useEffect(() => {
    loadCourse();
  }, [id]);

  useEffect(() => {
    if (!isDescriptionModalOpen) return undefined;

    const onEscape = event => {
      if (event.key === 'Escape') {
        setIsDescriptionModalOpen(false);
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onEscape);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onEscape);
    };
  }, [isDescriptionModalOpen]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('enroll') === '1') {
      setTimeout(() => {
        const el = document.getElementById('course-enroll-form');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  }, [location.search, course]);

  useEffect(() => {
    setSelectedModuleIds([]);
    setIsPaymentInstructionExpanded(false);
  }, [courseIntakeId]);

  const toggleModuleSelection = moduleId => {
    setSelectedModuleIds(prev =>
      prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId]
    );
  };

  const loadCourse = async () => {
    const res = await api.get(`/courses/${id}`);
    setCourse(res.data);
    const openIntakes = (res.data?.intakes || []).filter(isEnrollIntake);
    if (!openIntakes.length) return;

    const queryIntake = selectedIntakeFromQuery
      ? openIntakes.find(i => String(i.id) === String(selectedIntakeFromQuery))
      : null;

    setCourseIntakeId(String((queryIntake || openIntakes[0]).id));
  };

  const enroll = async e => {
    e.preventDefault();

    if (!paymentProof) {
      toast.error('Payment proof is required');
      return;
    }

    if (!courseIntakeId) {
      toast.error('Please select an intake/session');
      return;
    }

    if (packageType === 'individual_modules' && selectedModuleIds.length === 0) {
      toast.error('Select at least one module or switch to full package');
      return;
    }

    const form = new FormData();
    form.append('course_id', id);
    form.append('course_intake_id', courseIntakeId);
    form.append('full_name', fullName);
    form.append('email', email);
    form.append('phone_number', phone);
    form.append('level_of_studies', levelOfStudies);
    form.append('employment_status', employmentStatus);
    form.append('favorable_training_time', favorableTrainingTime);
    form.append('accounting_skill_level', accountingSkillLevel);
    form.append('excel_skill_level', excelSkillLevel);
    form.append('payment_method', paymentMethod);
    form.append('package_type', packageType);
    form.append('payment_plan', paymentPlan);
    form.append('payment_reference', paymentReference);
    form.append('additional_notes', notes);
    form.append('payment_proof', paymentProof);

    selectedModuleIds.forEach(moduleId => {
      form.append('selected_module_ids[]', moduleId);
    });

    applicationDocuments.forEach(file => {
      form.append('application_documents[]', file);
    });

    try {
      await api.post('/enrollments', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Enrollment submitted successfully');
      navigate('/student/dashboard');
    } catch (err) {
      const validationErrors = err.response?.data?.errors;
      const firstValidationError = validationErrors
        ? Object.values(validationErrors)?.[0]?.[0]
        : null;
      const msg = firstValidationError || err.response?.data?.message || 'Enrollment failed';
      toast.error(msg);
    }
  };

  if (!course) return <div className="p-5">Loading...</div>;

  const availableIntakes = (course.intakes || []).filter(isEnrollIntake);
  const hasSelectedIntakeFromList = availableIntakes.some(i => String(i.id) === String(courseIntakeId));
  const isIntakeLocked = Boolean(selectedIntakeFromQuery && hasSelectedIntakeFromList);
  const selectedIntake = availableIntakes.find(i => String(i.id) === String(courseIntakeId));
  const activeIntakeModules = (selectedIntake?.modules || []).filter(module => module.status === 'active');
  const fullPackageModule = activeIntakeModules.find(module => module.is_full_package);
  const selectableModules = activeIntakeModules.filter(module => !module.is_full_package);
  const selectedModuleRows = selectableModules.filter(module => selectedModuleIds.includes(module.id));
  const quoteAmount =
    packageType === 'individual_modules'
      ? selectedModuleRows.reduce((sum, module) => sum + Number(module.fee || 0), 0)
      : Number(fullPackageModule?.fee ?? course.fee ?? 0);
  const paymentInstruction =
    selectedIntake?.payment_instruction ||
    'Pay full amount upon registration, or 50% deposit = RWF 88,500. Call 0788393784 for assistance.';
  const paymentInstructionText = stripHtml(paymentInstruction);
  const hasLongPaymentInstruction = paymentInstructionText.length > 220;
  const paymentInstructionPreview = hasLongPaymentInstruction
    ? `${paymentInstructionText.slice(0, 220).trim()}...`
    : paymentInstructionText;
  const descriptionBlocks = parseDescriptionBlocks(course.description);
  const { previewBlocks, isTruncated } = getDescriptionPreview(descriptionBlocks);

  return (
    <>
      <TopBar />
      <main className="page-about">
        <div className="container">
          <Navbar />
        </div>

        <section className="lms-course-details-hero">
          <div className="lms-hero-shell">
            <div className="lms-hero-left">
              <p className="lms-hero-kicker">Course Information</p>
              <h1 className="lms-hero-title">
                <span className="lms-hero-title-blue">{course.title}</span>
              </h1>
              <div className="lms-hero-description">
                <CourseDescriptionContent blocks={isTruncated ? previewBlocks : descriptionBlocks} />
                {isTruncated && (
                  <button
                    type="button"
                    className="lms-description-read-more"
                    onClick={() => setIsDescriptionModalOpen(true)}
                  >
                    Read more
                  </button>
                )}
              </div>
              <div className="lms-hero-actions">
                <a href="#course-enroll-form" className="lms-hero-btn lms-hero-btn-primary">
                  Enroll Now
                </a>
                <a href="#course-enroll-form" className="lms-hero-btn lms-hero-btn-secondary">
                  Contact Team
                </a>
              </div>
            </div>

            <div className="lms-hero-right-wrap">
              <div className="lms-hero-right-card">
                <div className="lms-hero-card-head">
                  <span className="lms-hero-card-badge">ICB</span>
                  <strong>{course.category || 'Professional Course'}</strong>
                </div>

                <div className="lms-hero-info-row">
                  <span className="lms-hero-info-label">Trainer</span>
                  <span className="lms-hero-info-value">{course.trainer || 'To be announced'}</span>
                </div>
                <div className="lms-hero-info-row">
                  <span className="lms-hero-info-label">Duration</span>
                  <span className="lms-hero-info-value">{course.duration || 'TBA'}</span>
                </div>
                <div className="lms-hero-info-row">
                  <span className="lms-hero-info-label">Schedule</span>
                  <span className="lms-hero-info-value">{course.schedule || 'TBA'}</span>
                </div>
                <div className="lms-hero-info-row">
                  <span className="lms-hero-info-label">Tuition Fee</span>
                  <span className="lms-hero-info-value">
                    {course.fee_currency || 'RWF'} {course.fee}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="container">
          <div className="py-3 lms-course-details-wrap">
            <div className="lms-course-flow">
              <section className="lms-course-details-card">
                <h5 className="mb-3 fw-bold" style={{ color: '#0f2350' }}>What you will learn</h5>
                {(course.outline || []).length ? (
                  <ul className="lms-course-outline">
                    {(course.outline || []).map((item, i) => (
                      <li key={`${item}-${i}`}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">Detailed outline will be shared before class starts.</p>
                )}
              </section>

              <section className="lms-course-enroll-card" id="course-enroll-form">
                  <div className="lms-enroll-card-header">
                    <h5>Enroll now</h5>
                    <p>Secure your seat by completing the form below.</p>
                  </div>

                  <form onSubmit={enroll}>
                    {/* Personal info */}
                    <div className="lms-enroll-section">
                      <div className="lms-enroll-section-title">Personal information</div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Full names (as on ID or Passport)</label>
                          <input
                            required
                            className="form-control"
                            value={fullName}
                            placeholder="Your full name"
                            onChange={e => setFullName(e.target.value)}
                          />
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label">Email address</label>
                          <input
                            required
                            type="email"
                            className="form-control"
                            value={email}
                            placeholder="name@email.com"
                            onChange={e => setEmail(e.target.value)}
                          />
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label">Telephone number</label>
                          <input
                            required
                            className="form-control"
                            placeholder="+250 7xx xxx xxx"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Session */}
                    <div className="lms-enroll-section">
                      <div className="lms-enroll-section-title">Intake & session</div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Intake / Session</label>
                          {isIntakeLocked && selectedIntake ? (
                            <div className="lms-intake-locked">
                              ✓ {selectedIntake.name}
                              {(selectedIntake.start_date || selectedIntake.end_date) &&
                                ` · ${formatDate(selectedIntake.start_date)} – ${formatDate(selectedIntake.end_date)}`}
                            </div>
                          ) : (
                            <select
                              required
                              className="form-select"
                              value={courseIntakeId}
                              onChange={e => setCourseIntakeId(e.target.value)}
                            >
                              <option value="">Select intake / session</option>
                              {availableIntakes.map(i => (
                                <option key={i.id} value={i.id}>
                                  {i.name}
                                  {i.start_date || i.end_date
                                    ? ` (${formatDate(i.start_date)} – ${formatDate(i.end_date)})`
                                    : ''}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div className="col-12">
                          <label className="form-label">Favorable training time</label>
                          <select
                            required
                            className="form-select"
                            value={favorableTrainingTime}
                            onChange={e => setFavorableTrainingTime(e.target.value)}
                          >
                            <option value="">Select preferred schedule</option>
                            {TRAINING_TIME_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Package */}
                    <div className="lms-enroll-section">
                      <div className="lms-enroll-section-title">Training package</div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Package type</label>
                          <div className="d-flex flex-wrap gap-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="package-full"
                                name="packageType"
                                checked={packageType === 'full_package'}
                                onChange={() => setPackageType('full_package')}
                              />
                              <label className="form-check-label" htmlFor="package-full">
                                Full package
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="package-modules"
                                name="packageType"
                                checked={packageType === 'individual_modules'}
                                onChange={() => setPackageType('individual_modules')}
                                disabled={!selectableModules.length}
                              />
                              <label className="form-check-label" htmlFor="package-modules">
                                Individual modules
                              </label>
                            </div>
                          </div>
                          {!selectableModules.length && (
                            <div className="form-text">No module-level pricing configured for this intake yet.</div>
                          )}
                        </div>

                        {packageType === 'individual_modules' && selectableModules.length > 0 && (
                          <div className="col-12">
                            <label className="form-label">Select your modules</label>
                            <div className="border rounded-3 p-2">
                              {selectableModules.map(module => (
                                <div key={module.id} className="form-check mb-2">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`module-${module.id}`}
                                    checked={selectedModuleIds.includes(module.id)}
                                    onChange={() => toggleModuleSelection(module.id)}
                                  />
                                  <label className="form-check-label" htmlFor={`module-${module.id}`}>
                                    {module.name}
                                    {module.duration_weeks ? ` (${module.duration_weeks} week${module.duration_weeks > 1 ? 's' : ''})` : ''}
                                    {' - '}
                                    {course.fee_currency || 'RWF'} {Number(module.fee || 0).toLocaleString()}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="col-12">
                          <label className="form-label">Estimated total</label>
                          <div className="lms-intake-locked">
                            {course.fee_currency || 'RWF'} {Number(quoteAmount || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Academic profile */}
                    <div className="lms-enroll-section">
                      <div className="lms-enroll-section-title">Academic profile</div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Level of studies</label>
                          <select
                            required
                            className="form-select"
                            value={levelOfStudies}
                            onChange={e => setLevelOfStudies(e.target.value)}
                          >
                            <option value="">Select level</option>
                            {LEVEL_OF_STUDIES_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12">
                          <label className="form-label">Employment status</label>
                          <select
                            required
                            className="form-select"
                            value={employmentStatus}
                            onChange={e => setEmploymentStatus(e.target.value)}
                          >
                            <option value="">Select status</option>
                            {EMPLOYMENT_STATUS_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label">Accounting skill level</label>
                          <select
                            required
                            className="form-select"
                            value={accountingSkillLevel}
                            onChange={e => setAccountingSkillLevel(e.target.value)}
                          >
                            <option value="">Select level</option>
                            {SKILL_LEVEL_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label">Excel skill level</label>
                          <select
                            required
                            className="form-select"
                            value={excelSkillLevel}
                            onChange={e => setExcelSkillLevel(e.target.value)}
                          >
                            <option value="">Select level</option>
                            {SKILL_LEVEL_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Payment */}
                    <div className="lms-enroll-section">
                      <div className="lms-enroll-section-title">Payment</div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Payment method</label>
                          <select
                            required
                            className="form-select"
                            value={paymentMethod}
                            onChange={e => setPaymentMethod(e.target.value)}
                          >
                            <option value="">Select payment method</option>
                            {PAYMENT_METHOD_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          <div className="lms-payment-notice">
                            <strong>Payment instructions:</strong>{' '}
                            {hasLongPaymentInstruction && !isPaymentInstructionExpanded ? (
                              <>
                                {paymentInstructionPreview}{' '}
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 align-baseline"
                                  onClick={() => setIsPaymentInstructionExpanded(true)}
                                >
                                  Read more
                                </button>
                              </>
                            ) : (
                              <>
                                <div
                                  className="mt-2"
                                  dangerouslySetInnerHTML={createSafeHtml(paymentInstruction)}
                                />
                                {hasLongPaymentInstruction && (
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0"
                                    onClick={() => setIsPaymentInstructionExpanded(false)}
                                  >
                                    Show less
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label">Payment plan</label>
                          <select
                            required
                            className="form-select"
                            value={paymentPlan}
                            onChange={e => setPaymentPlan(e.target.value)}
                          >
                            <option value="full">Full payment</option>
                            <option value="installment">Installment plan</option>
                          </select>
                        </div>
                        <div className="col-sm-6">
                          <label className="form-label">Payment reference (optional)</label>
                          <input
                            className="form-control"
                            value={paymentReference}
                            placeholder="MoMo/BK transaction reference"
                            onChange={e => setPaymentReference(e.target.value)}
                          />
                        </div>
                        <div className="col-12">
                          <label className="form-label">Upload proof of payment</label>
                          <input
                            type="file"
                            required
                            accept=".pdf,.png,.jpg,.jpeg"
                            className="form-control"
                            onChange={e => setPaymentProof(e.target.files?.[0])}
                          />
                          <div className="form-text">Accepted: PDF, PNG, JPG · Max 5 MB</div>
                        </div>
                        <div className="col-12">
                          <label className="form-label">Supporting documents (optional)</label>
                          <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                            className="form-control"
                            onChange={e => setApplicationDocuments(Array.from(e.target.files || []))}
                          />
                          <div className="form-text">Accepted: PDF, DOC, DOCX, TXT, PNG, JPG · Max 5 MB each</div>
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="lms-enroll-section" style={{ marginBottom: '0.55rem' }}>
                      <div className="lms-enroll-section-title">Additional notes (optional)</div>
                      <textarea
                        className="form-control"
                        placeholder="Any additional information relevant to your enrollment…"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <button className="lms-submit-btn" type="submit">
                      Submit enrollment →
                    </button>
                  </form>
              </section>
            </div>
          </div>
        </div>

        {isDescriptionModalOpen && (
          <div
            className="lms-description-modal-overlay"
            role="presentation"
            onClick={() => setIsDescriptionModalOpen(false)}
          >
            <div
              className="lms-description-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="description-modal-title"
              onClick={event => event.stopPropagation()}
            >
              <div className="lms-description-modal-header">
                <h2 id="description-modal-title">Course description</h2>
                <button
                  type="button"
                  className="lms-description-modal-close"
                  aria-label="Close description"
                  onClick={() => setIsDescriptionModalOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="lms-description-modal-body">
                <CourseDescriptionContent blocks={descriptionBlocks} />
              </div>
            </div>
          </div>
        )}
        <Footer />
      </main>
    </>
  );
};

export default CourseDetailsPage;
